// ============================================================================
// HAI Reporting Tool - Infection Trend Chart Component
// Displays 8-week historical infection rate trends
// ============================================================================

import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { formatRate, aggregateHCAISurveillanceCases } from '@/utils/calculations';
import type { DeviceSnapshot, HAIRecord, HCAISurveillanceCase } from '@/types';

interface WeeklyData {
  week: string;
  uti: number;
  cabsi: number;
  vap: number;
}

/**
 * Chart component showing infection rate trends over 8 weeks
 */
export function InfectionTrendChart() {
  const [weeklyData, setWeeklyData] = useState<WeeklyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTrendData();
  }, []);

  const loadTrendData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate 8 weeks back
      const eightWeeksAgo = new Date();
      eightWeeksAgo.setDate(eightWeeksAgo.getDate() - 56);

      // Generate array of weeks
      const weeks: WeeklyData[] = [];
      const currentDate = new Date(eightWeeksAgo);

      for (let i = 0; i < 8; i++) {
        const weekStart = new Date(currentDate);
        const weekEnd = new Date(currentDate);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const weekStartStr = weekStart.toISOString().split('T')[0];
        const weekEndStr = weekEnd.toISOString().split('T')[0];
        const weekLabel = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;

        // Fetch data for this week
        const deviceQuery = query(
          collection(db, 'deviceSnapshots'),
          where('date', '>=', weekStartStr),
          where('date', '<=', weekEndStr)
        ) as any;

        const haiQuery = query(
          collection(db, 'haiRecords'),
          where('periodEnd', '>=', weekStartStr),
          where('periodEnd', '<=', weekEndStr)
        ) as any;

        const hcaiQuery = query(
          collection(db, 'hcaiCases'),
          where('createdAt', '>=', Timestamp.fromDate(weekStart)),
          where('createdAt', '<=', Timestamp.fromDate(weekEnd)),
          orderBy('createdAt', 'asc')
        ) as any;

        const [deviceDocs, haiDocs, hcaiDocs] = await Promise.all([
          getDocs(deviceQuery),
          getDocs(haiQuery),
          getDocs(hcaiQuery)
        ]);

        const devices = deviceDocs.docs.map(doc => doc.data()) as DeviceSnapshot[];
        const records = haiDocs.docs.map(doc => doc.data()) as HAIRecord[];
        const surveillanceCases = hcaiDocs.docs.map(doc => {
          const rawData = doc.data() as any;
          return {
            id: doc.id,
            ...rawData,
            createdAt: rawData.createdAt?.toDate?.() || new Date(),
            updatedAt: rawData.updatedAt?.toDate?.() || new Date()
          } as HCAISurveillanceCase;
        });
        const aggregatedSurveillance = aggregateHCAISurveillanceCases(surveillanceCases);

        // Calculate device days
        const totalCVPDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.cvp || 0), 0);
        const totalCatheterDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.urinaryCatheter || 0), 0);
        const totalVentilatorDays = devices.reduce((sum, d) => sum + (d.patientsWithDevice?.ventilator || 0), 0);

        // Calculate HCAI cases
        const totalCABSI = records.reduce((sum, r) => sum + (r.cases?.cabsi || 0), 0) + aggregatedSurveillance.cabsi;
        const totalUTI = records.reduce((sum, r) => sum + (r.cases?.uti || 0), 0) + aggregatedSurveillance.uti;
        const totalVAP = records.reduce((sum, r) => sum + (r.cases?.vap || 0), 0) + aggregatedSurveillance.vap;

        // Calculate rates (per 1000 device days)
        const utiRate = totalCatheterDays > 0 ? (totalUTI / totalCatheterDays) * 1000 : 0;
        const cabsiRate = totalCVPDays > 0 ? (totalCABSI / totalCVPDays) * 1000 : 0;
        const vapRate = totalVentilatorDays > 0 ? (totalVAP / totalVentilatorDays) * 1000 : 0;

        weeks.push({
          week: weekLabel,
          uti: Number(utiRate.toFixed(2)),
          cabsi: Number(cabsiRate.toFixed(2)),
          vap: Number(vapRate.toFixed(2))
        });

        currentDate.setDate(currentDate.getDate() + 7);
      }

      setWeeklyData(weeks);
    } catch (err) {
      console.error('Error loading trend data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trend data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading trend data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-danger-50 border border-danger-200">
        <p className="text-sm text-danger-700">Error loading trend data: {error}</p>
      </div>
    );
  }

  // Find max rate for scaling
  const maxRate = Math.max(
    ...weeklyData.flatMap(w => [w.uti, w.cabsi, w.vap])
  ) || 5;

  const targetRate = 3; // CDC target

  return (
    <div className="card">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Infection Rate Trends (8 Weeks)</h3>
        <p className="text-sm text-gray-600 mt-1">Rates per 1000 device-days</p>
      </div>

      {/* Simple Line Chart Representation */}
      <div className="space-y-6">
        {/* CAUTI Rate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">💉 CAUTI Rate</span>
            <span className="text-sm font-semibold text-primary-600">
              {weeklyData.length > 0 ? formatRate(weeklyData[weeklyData.length - 1].uti) : '0'}/1000
            </span>
          </div>
          <div className="h-12 bg-gray-100 rounded-lg p-2 flex items-center gap-1 overflow-x-auto">
            {weeklyData.map((week, idx) => {
              const heightPercent = (week.uti / maxRate) * 100;
              const isAboveTarget = week.uti > targetRate;
              return (
                <div
                  key={idx}
                  className="flex-1 flex flex-col items-center justify-end"
                  title={`Week ${week.week}: ${week.uti}/1000 CAUTI`}
                >
                  <div
                    className={`w-full rounded-t ${
                      isAboveTarget ? 'bg-danger-500' : 'bg-primary-500'
                    }`}
                    style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '8px' }}
                  />
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-500 mt-1">Target: ≤2.5/1000</div>
        </div>

        {/* CLABSI Rate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">🩸 CLABSI Rate</span>
            <span className="text-sm font-semibold text-danger-600">
              {weeklyData.length > 0 ? formatRate(weeklyData[weeklyData.length - 1].cabsi) : '0'}/1000
            </span>
          </div>
          <div className="h-12 bg-gray-100 rounded-lg p-2 flex items-center gap-1 overflow-x-auto">
            {weeklyData.map((week, idx) => {
              const heightPercent = (week.cabsi / maxRate) * 100;
              const isAboveTarget = week.cabsi > targetRate;
              return (
                <div
                  key={idx}
                  className="flex-1 flex flex-col items-center justify-end"
                  title={`Week ${week.week}: ${week.cabsi}/1000 CLABSI`}
                >
                  <div
                    className={`w-full rounded-t ${
                      isAboveTarget ? 'bg-danger-500' : 'bg-success-500'
                    }`}
                    style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '8px' }}
                  />
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-500 mt-1">Target: ≤1.5/1000</div>
        </div>

        {/* VAP Rate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">🫁 VAP Rate</span>
            <span className="text-sm font-semibold text-warning-600">
              {weeklyData.length > 0 ? formatRate(weeklyData[weeklyData.length - 1].vap) : '0'}/1000
            </span>
          </div>
          <div className="h-12 bg-gray-100 rounded-lg p-2 flex items-center gap-1 overflow-x-auto">
            {weeklyData.map((week, idx) => {
              const heightPercent = (week.vap / maxRate) * 100;
              const isAboveTarget = week.vap > targetRate;
              return (
                <div
                  key={idx}
                  className="flex-1 flex flex-col items-center justify-end"
                  title={`Week ${week.week}: ${week.vap}/1000`}
                >
                  <div
                    className={`w-full rounded-t ${
                      isAboveTarget ? 'bg-danger-500' : 'bg-warning-500'
                    }`}
                    style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '8px' }}
                  />
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-500 mt-1">Target: ≤2.0/1000</div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-success-500"></div>
          <span className="text-gray-600">On Target</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-danger-500"></div>
          <span className="text-gray-600">Above Target</span>
        </div>
      </div>

      {/* Data Table (Optional - for detailed view) */}
      <div className="mt-6 pt-4 border-t border-gray-200 overflow-x-auto">
        <table className="min-w-full text-xs divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left text-gray-700 font-medium">Week</th>
              <th className="px-2 py-2 text-left text-gray-700 font-medium">CAUTI</th>
              <th className="px-2 py-2 text-left text-gray-700 font-medium">CLABSI</th>
              <th className="px-2 py-2 text-left text-gray-700 font-medium">VAP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {weeklyData.map((week, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-2 py-2 text-gray-900 font-medium">{week.week}</td>
                <td className={`px-2 py-2 ${week.uti > targetRate ? 'text-danger-600 font-semibold' : 'text-gray-700'}`}>
                  {formatRate(week.uti)}
                </td>
                <td className={`px-2 py-2 ${week.cabsi > targetRate ? 'text-danger-600 font-semibold' : 'text-gray-700'}`}>
                  {formatRate(week.cabsi)}
                </td>
                <td className={`px-2 py-2 ${week.vap > targetRate ? 'text-danger-600 font-semibold' : 'text-gray-700'}`}>
                  {formatRate(week.vap)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
